import AutoDialerAPI from 'dashboard/api/whatsappSip/autodialer';

export const state = {
  campaigns: [],
  currentCampaign: null,
  contacts: [],
  contactsMeta: { total: 0, page: 1, per_page: 10, total_pages: 0 },
  campaignsMeta: { total: 0, page: 1, per_page: 10, total_pages: 0 },
  campaignsStats: { total: 0, running: 0, completed: 0, paused: 0, failed: 0 },
  stats: null,
  campaignDraft: {
    dataSource: 'tags',
    sourceConfig: {},
    contacts: [],
    placeholders: [],
    permissions: {},
    name: '',
    ai_flow_id: null,
    permission_timeout: 120,
    max_retries: 1,
    max_concurrent: 1,
    scheduleMode: 'now',
    scheduledAt: null,
    endDate: '',
    recurrenceType: 'daily',
    selectedDays: [],
  },
  campaignError: null,
  permissionChanged: null,
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isStarting: false,
    isFetchingContacts: false,
    isAddingContacts: false,
    isFetchingStats: false,
  },
};

export const getters = {
  getCampaigns: $state => $state.campaigns,
  getCurrentCampaign: $state => $state.currentCampaign,
  getContacts: $state => $state.contacts,
  getCampaignsMeta: $state => $state.campaignsMeta,
  getCampaignsStats: $state => $state.campaignsStats,
  getContactsMeta: $state => $state.contactsMeta,
  getStats: $state => $state.stats,
  getCampaignDraft: $state => $state.campaignDraft,
  getDraftPlaceholders: $state => $state.campaignDraft.placeholders,
  getUIFlags: $state => $state.uiFlags,
  getCampaignError: $state => $state.campaignError,
  getPermissionChanged: $state => $state.permissionChanged,
};

export const actions = {
  async fetchCampaigns({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await AutoDialerAPI.getCampaigns(params);
      commit('SET_CAMPAIGNS', data.campaigns || []);
      if (data.total !== undefined) {
        commit('SET_CAMPAIGNS_META', {
          total: data.total,
          page: data.page || 1,
          per_page: data.per_page || 10,
          total_pages: data.total_pages || 0,
        });
      }
      if (data.stats) {
        commit('SET_CAMPAIGNS_STATS', data.stats);
      }
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async showCampaign({ commit }, id) {
    const { data } = await AutoDialerAPI.showCampaign(id);
    const campaign = data.campaign || data;
    commit('SET_CURRENT_CAMPAIGN', campaign);
    return campaign;
  },

  async createCampaign({ commit }, campaignData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await AutoDialerAPI.createCampaign({
        campaign: campaignData,
      });
      const campaign = data.campaign || data;
      commit('ADD_CAMPAIGN', campaign);
      return campaign;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async updateCampaign({ commit }, { id, data: updateData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await AutoDialerAPI.updateCampaign(id, {
        campaign: updateData,
      });
      const campaign = data.campaign || data;
      commit('UPDATE_CAMPAIGN', campaign);
      return campaign;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async deleteCampaign({ commit }, id) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await AutoDialerAPI.deleteCampaign(id);
      commit('DELETE_CAMPAIGN', id);
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  async startCampaign({ commit, dispatch }, id) {
    commit('SET_UI_FLAG', { isStarting: true });
    try {
      await AutoDialerAPI.startCampaign(id);
      await dispatch('showCampaign', id);
    } finally {
      commit('SET_UI_FLAG', { isStarting: false });
    }
  },

  async pauseCampaign({ commit, dispatch }, id) {
    commit('SET_UI_FLAG', { isStarting: true });
    try {
      await AutoDialerAPI.pauseCampaign(id);
      await dispatch('showCampaign', id);
    } finally {
      commit('SET_UI_FLAG', { isStarting: false });
    }
  },

  async resumeCampaign({ commit, dispatch }, id) {
    commit('SET_UI_FLAG', { isStarting: true });
    try {
      await AutoDialerAPI.resumeCampaign(id);
      await dispatch('showCampaign', id);
    } finally {
      commit('SET_UI_FLAG', { isStarting: false });
    }
  },

  async fetchContacts({ commit }, payload) {
    commit('SET_UI_FLAG', { isFetchingContacts: true });
    try {
      // Support both: fetchContacts(id) and fetchContacts({ campaignId, params })
      const isLegacy = typeof payload !== 'object' || !payload.campaignId;
      const campaignId = isLegacy ? payload : payload.campaignId;
      const params = isLegacy ? {} : payload.params || {};

      const { data } = await AutoDialerAPI.getContacts(campaignId, params);
      commit('SET_CONTACTS', data.contacts || []);
      if (data.total !== undefined) {
        commit('SET_CONTACTS_META', {
          total: data.total,
          page: data.page || 1,
          per_page: data.per_page || 10,
          total_pages: data.total_pages || 0,
        });
      }
    } finally {
      commit('SET_UI_FLAG', { isFetchingContacts: false });
    }
  },

  async addContacts({ commit, dispatch }, { campaignId, contacts }) {
    commit('SET_UI_FLAG', { isAddingContacts: true });
    try {
      const { data } = await AutoDialerAPI.addContacts(campaignId, contacts);
      await dispatch('fetchContacts', campaignId);
      await dispatch('showCampaign', campaignId);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isAddingContacts: false });
    }
  },

  async replaceContacts({ commit, dispatch }, { campaignId, contacts }) {
    commit('SET_UI_FLAG', { isAddingContacts: true });
    try {
      const { data } = await AutoDialerAPI.replaceContacts(
        campaignId,
        contacts
      );
      await dispatch('fetchContacts', campaignId);
      await dispatch('showCampaign', campaignId);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isAddingContacts: false });
    }
  },

  async removeContact({ dispatch }, { campaignId, contactId }) {
    await AutoDialerAPI.removeContact(campaignId, contactId);
    await dispatch('fetchContacts', campaignId);
    await dispatch('showCampaign', campaignId);
  },

  async fetchStats({ commit }, campaignId) {
    commit('SET_UI_FLAG', { isFetchingStats: true });
    try {
      const { data } = await AutoDialerAPI.getStats(campaignId);
      commit('SET_STATS', data.stats || data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingStats: false });
    }
  },

  async preflightContacts(_, phones) {
    return AutoDialerAPI.preflightContacts(phones);
  },

  saveDraft({ commit }, data) {
    commit('SAVE_DRAFT', data);
  },

  clearDraft({ commit }) {
    commit('CLEAR_DRAFT');
  },

  // Called by ActionCable when Go engine sends a real-time event.
  handleRealtimeEvent({ commit, state: $state, dispatch }, event) {
    const { event_type: eventType, campaign_id: campaignId, data } = event;

    switch (eventType) {
      case 'campaign_status_changed':
        commit('UPDATE_CAMPAIGN_STATUS', {
          campaignId,
          status: data.status,
          scheduledAt: data.scheduled_at,
        });
        commit('CLEAR_CAMPAIGN_ERROR');
        // Refetch full campaign list from API to ensure UI is in sync
        // (TanStack Table may not react to in-place Vuex mutations).
        dispatch('fetchCampaigns');
        break;

      case 'campaign_error':
        commit('SET_CAMPAIGN_ERROR', {
          campaignId,
          errorCode: data.error_code,
          errorMessage: data.error_message,
        });
        break;

      case 'permission_changed':
        commit('SET_PERMISSION_CHANGED', {
          waId: data.wa_id,
          response: data.response,
          permanent: data.permanent,
        });
        break;

      case 'contact_status_changed':
        commit('UPDATE_CONTACT_STATUS', {
          contactId: data.contact_id,
          status: data.status,
        });
        if ($state.currentCampaign?.id === campaignId) {
          dispatch('showCampaign', campaignId);
        }
        break;

      default:
        break;
    }
  },
};

export const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  SET_CAMPAIGNS($state, campaigns) {
    $state.campaigns = campaigns;
  },
  SET_CURRENT_CAMPAIGN($state, campaign) {
    $state.currentCampaign = campaign;
  },
  ADD_CAMPAIGN($state, campaign) {
    $state.campaigns = [campaign, ...$state.campaigns];
  },
  UPDATE_CAMPAIGN($state, campaign) {
    const index = $state.campaigns.findIndex(c => c.id === campaign.id);
    if (index !== -1) {
      $state.campaigns.splice(index, 1, {
        ...$state.campaigns[index],
        ...campaign,
      });
    }
    if ($state.currentCampaign?.id === campaign.id) {
      $state.currentCampaign = { ...$state.currentCampaign, ...campaign };
    }
  },
  UPDATE_CAMPAIGN_STATUS($state, { campaignId, status, scheduledAt }) {
    const index = $state.campaigns.findIndex(c => c.id === campaignId);
    if (index !== -1) {
      const update = { ...$state.campaigns[index], status };
      if (scheduledAt !== undefined) {
        update.scheduled_at = scheduledAt;
      }
      $state.campaigns.splice(index, 1, update);
    }
    if ($state.currentCampaign?.id === campaignId) {
      const update = { ...$state.currentCampaign, status };
      if (scheduledAt !== undefined) {
        update.scheduled_at = scheduledAt;
      }
      $state.currentCampaign = update;
    }
  },
  UPDATE_CONTACT_STATUS($state, { contactId, status }) {
    $state.contacts = $state.contacts.map(c =>
      c.id === contactId ? { ...c, status } : c
    );
  },
  DELETE_CAMPAIGN($state, id) {
    $state.campaigns = $state.campaigns.filter(c => c.id !== id);
  },
  SAVE_DRAFT($state, data) {
    $state.campaignDraft = { ...$state.campaignDraft, ...data };
  },
  CLEAR_DRAFT($state) {
    $state.campaignDraft = {
      dataSource: 'tags',
      sourceConfig: {},
      contacts: [],
      placeholders: [],
      permissions: {},
      name: '',
      ai_flow_id: null,
      permission_timeout: 120,
      max_retries: 1,
      max_concurrent: 1,
      scheduleMode: 'now',
      scheduledAt: null,
      endDate: '',
      recurrenceType: 'daily',
      selectedDays: [],
    };
  },
  SET_CAMPAIGNS_META($state, meta) {
    $state.campaignsMeta = meta;
  },
  SET_CAMPAIGNS_STATS($state, stats) {
    $state.campaignsStats = stats;
  },
  SET_CONTACTS($state, contacts) {
    $state.contacts = contacts;
  },
  SET_CONTACTS_META($state, meta) {
    $state.contactsMeta = meta;
  },
  SET_STATS($state, stats) {
    $state.stats = stats;
  },
  SET_CAMPAIGN_ERROR($state, { campaignId, errorCode, errorMessage }) {
    $state.campaignError = { campaignId, errorCode, errorMessage };
  },
  CLEAR_CAMPAIGN_ERROR($state) {
    $state.campaignError = null;
  },
  SET_PERMISSION_CHANGED($state, { waId, response, permanent }) {
    $state.permissionChanged = { waId, response, permanent };
  },
  CLEAR_PERMISSION_CHANGED($state) {
    $state.permissionChanged = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
